import React, { useRef } from 'react';
import {
  Box,
  Badge,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Button,
  VStack,
  useOutsideClick,
  useDisclosure,
  Portal,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';

export interface Option {
  value: string;
  label: string;
  colorScheme?: string;
}

export interface EditableSelectProps {
  value: string;
  options: Option[];
  onChange: (value: string) => void;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
}

const getColorScheme = (value: string): string => {
  switch (value) {
    case 'Resolved':
      return 'green';
    case 'In Review':
      return 'blue';
    case "Won't Fix":
      return 'purple';
    default:
      return 'gray';
  }
};

const EditableSelect: React.FC<EditableSelectProps> = ({
  value,
  options,
  onChange,
  placeholder = 'Select...',
  size = 'md',
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Find the current option
  const currentOption = options.find((option: Option) => option.value === value);
  const displayValue = currentOption?.label || value || placeholder;
  const colorScheme = currentOption?.colorScheme || getColorScheme(value);

  // Close the popover when clicking outside
  useOutsideClick({
    ref: popoverRef,
    handler: () => isOpen && onClose(),
  });

  const handleSelect = (newValue: string) => {
    onChange(newValue);
    onClose();
  };

  return (
    <Box position="relative" display="inline-block">
      <Popover
        isOpen={isOpen}
        onClose={onClose}
        placement="bottom-start"
        closeOnBlur={false}
        isLazy
      >
        <PopoverTrigger>
          <Box 
            ref={triggerRef}
            onClick={onOpen}
            cursor="pointer"
            display="flex"
            alignItems="center"
            _hover={{ opacity: 0.8 }}
          >
            <Badge
              colorScheme={colorScheme}
              p={size === 'sm' ? 1 : 2}
              borderRadius="md"
              fontSize={size === 'sm' ? 'xs' : 'sm'}
              display="flex"
              alignItems="center"
              gap={1}
            >
              {displayValue}
              <ChevronDownIcon boxSize={3} ml={1} />
            </Badge>
          </Box>
        </PopoverTrigger>
        <Portal>
          <PopoverContent width="auto" ref={popoverRef}>
            <PopoverBody p={1}>
              <VStack align="stretch" spacing={1}>
                {options.map((option: Option) => (
                  <Button
                    key={option.value}
                    size={size}
                    variant={option.value === value ? 'solid' : 'ghost'}
                    colorScheme={option.colorScheme || getColorScheme(option.value)}
                    justifyContent="flex-start"
                    onClick={() => handleSelect(option.value)}
                    py={1}
                    px={2}
                    height="auto"
                    fontWeight={option.value === value ? 'bold' : 'normal'}
                  >
                    {option.label}
                  </Button>
                ))}
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    </Box>
  );
};

export default EditableSelect; 